import classNames from "classnames";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { useOnClickOutside } from "usehooks-ts";
import { CalendarDateIcon, ChevronDownIcon } from "@vetty/icons";
import selectStyles from "@/components/select/Select.module.scss";
import { Calendar } from "@/components";
import {
  dateRangeToAnalyticsBounds,
  formatStatisticsFilterLabel,
  getRollingBounds,
  presetToDateRange,
  startOfDay,
} from "../../_utils/statisticsDateFilter.util";
import styles from "./StatisticsDateRangePicker.module.scss";

export type StatisticsDateRangePickerChange = {
  start: Date;
  end: Date;
};

type StatisticsDateRangePickerProps = {
  onChange?: (value: StatisticsDateRangePickerChange) => void;
};

export function StatisticsDateRangePicker({
  onChange,
}: StatisticsDateRangePickerProps) {
  const id = useId();
  const wrapperRef = useRef<HTMLDivElement>(null!);
  /** Keeps latest callback without retriggering emitChange when parent re-renders. */
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [isOpen, setIsOpen] = useState(false);
  /** `7` = rolling last 7 days; `null` = custom range from calendar */
  const [preset, setPreset] = useState<7 | null>(7);
  const [range, setRange] = useState<DateRange | undefined>(() =>
    presetToDateRange(7),
  );

  useOnClickOutside(wrapperRef, () => setIsOpen(false));

  const emitChange = useCallback(() => {
    const notify = onChangeRef.current;
    if (!notify) return;
    if (preset !== null) {
      notify(getRollingBounds(preset));
      return;
    }
    if (!range?.from || range.to === undefined) return;
    const b = dateRangeToAnalyticsBounds(range);
    if (b) notify(b);
  }, [preset, range]);

  useEffect(() => {
    emitChange();
  }, [emitChange]);

  const label = formatStatisticsFilterLabel(preset, range);

  const todayStart = startOfDay(new Date());
  const statisticsCalendarEndMonth = new Date(
    todayStart.getFullYear(),
    todayStart.getMonth(),
    1,
  );

  const handleCalendarSelect = (next: DateRange | undefined) => {
    setPreset(null);
    setRange(next);
    /* Do not close on selection — range needs two clicks; closing here caused flaky UX. */
  };

  const handleRootClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('[data-select-field="true"]')) return;
    if (target.closest('[data-select-dropdown="true"]')) return;
    setIsOpen((o) => !o);
  };

  return (
    <div ref={wrapperRef} className={selectStyles["comp-wrap"]}>
      <div
        onClick={handleRootClick}
        className={classNames(
          selectStyles["comp"],
          selectStyles["comp--variant-secondary"],
          selectStyles["comp--size-medium"],
          selectStyles["comp--with-start-and-end-content"],
          isOpen && selectStyles["comp--open"],
        )}
      >
        <span className={selectStyles["comp__content"]} aria-hidden>
          <CalendarDateIcon style={{ width: 20, height: 20 }} />
        </span>

        <button
          id={`${id}-trigger`}
          type="button"
          data-select-field="true"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((o) => !o);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setIsOpen(false);
          }}
          className={selectStyles["comp__field"]}
        >
          <span className={selectStyles["comp__value"]}>{label}</span>
        </button>

        <span
          className={classNames(
            selectStyles["comp__content"],
            selectStyles["comp__content--end"],
            isOpen && selectStyles["comp__content--open"],
          )}
          aria-hidden
        >
          <ChevronDownIcon style={{ width: 20, height: 20 }} />
        </span>

        {isOpen ? (
          <div
            className={classNames(
              selectStyles["comp__dropdown"],
              selectStyles["comp__dropdown--variant-secondary"],
              selectStyles["comp__dropdown--size-medium"],
              selectStyles["comp__dropdown--placement-bottom"],
              styles["pickerDropdown"],
            )}
            data-select-dropdown="true"
            role="dialog"
            aria-label="Choose date range"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={styles["calendarSlot"]}>
              <Calendar
                mode="range"
                resetOnSelect
                selected={range}
                onSelect={handleCalendarSelect}
                numberOfMonths={1}
                defaultMonth={range?.to ?? range?.from ?? todayStart}
                endMonth={statisticsCalendarEndMonth}
                disabled={{ after: todayStart }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
